/*
 * Painting offer: what each painting line costs once the stamps land.
 *
 * Pure maths on top of offer-test-rule.js, no DOM and no network. The product
 * page receipt and the side cart load this one file, so the prices they show
 * come from the same rule the stamping engine writes.
 *
 *   price(arm, lines) -> { value, lines: [...], original, final, discount }
 *
 * Each input line: { key, quantity, price (unit price in cents, before any
 * discount), stamped (optional, as for the rule) }. Lines that share a key are
 * one cart line and are merged first.
 *
 * Each output line keeps the input's key, quantity and unit price, and adds
 * `parts`: the cart lines this line becomes once stamped. A line whose units
 * all get the same treatment stays one part. A line the stamps split (some
 * units discounted, some not) becomes two parts, the way the engine splits it.
 *
 * Money is integer cents, rounded the way the store charges (measured on
 * real carts, 2 Oct): the percentage comes off each unit, rounded down to the
 * cent, and a line costs its unit price times its quantity. 30% of 29.95 is
 * 8.98 off, so the unit costs 20.97, and two on one line cost 41.94.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory(require('./offer-test-rule.js'));
  // In the browser the rule is read when first needed, so load order between
  // the two files does not matter.
  else root.OmdOfferPricing = factory(null, root);
})(typeof self !== 'undefined' ? self : this, function (ruleModule, root) {
  function rule() {
    return ruleModule || root.OmdOfferRule;
  }

  // Percentage off per arm and stamp value. Mirrors the store's discounts.
  var PERCENT = { A: { '20': 20, '30': 30 } };

  function percentFor(arm, value) {
    return (PERCENT[arm] && PERCENT[arm][value]) || 0;
  }

  // The discount on one unit, rounded down to the cent. Inputs are whole
  // cents, so unit * percent is a whole number and this is the only rounding.
  function unitDiscount(unit, percent) {
    return Math.floor((unit * percent) / 100);
  }

  function cents(value) {
    return Math.max(0, Math.round(Number(value) || 0));
  }

  function merge(lines) {
    var byKey = {};
    var order = [];
    (lines || []).forEach(function (line) {
      var quantity = Math.max(0, Math.floor(Number(line.quantity) || 0));
      if (!byKey[line.key]) {
        byKey[line.key] = { key: line.key, quantity: 0, price: cents(line.price), stamped: !!line.stamped };
        order.push(line.key);
      }
      byKey[line.key].quantity += quantity;
      if (line.stamped) byKey[line.key].stamped = true;
    });
    return order.map(function (key) { return byKey[key]; });
  }

  function part(quantity, unit, value, percent) {
    var total = quantity * unit;
    var discount = percent ? quantity * unitDiscount(unit, percent) : 0;
    return {
      quantity: quantity,
      value: percent ? value : '',
      percent: percent,
      original: total,
      discount: discount,
      final: total - discount
    };
  }

  function price(arm, lines) {
    var merged = merge(lines).filter(function (line) { return line.quantity > 0; });
    var stamps = rule().computeStamps(arm, merged);
    var percent = percentFor(arm, stamps.value);
    var totals = { original: 0, discount: 0, final: 0 };

    var out = merged.map(function (line) {
      var stamped = Math.min(line.quantity, stamps.stamps[line.key] || 0);
      var parts = [];
      if (stamped > 0) parts.push(part(stamped, line.price, stamps.value, percent));
      if (line.quantity - stamped > 0) parts.push(part(line.quantity - stamped, line.price, '', 0));
      var result = { key: line.key, quantity: line.quantity, price: line.price, parts: parts, original: 0, discount: 0, final: 0 };
      parts.forEach(function (p) {
        result.original += p.original;
        result.discount += p.discount;
        result.final += p.final;
      });
      totals.original += result.original;
      totals.discount += result.discount;
      totals.final += result.final;
      return result;
    });

    return {
      value: stamps.value,
      percent: percent,
      lines: out,
      original: totals.original,
      discount: totals.discount,
      final: totals.final
    };
  }

  return { PERCENT: PERCENT, percentFor: percentFor, unitDiscount: unitDiscount, price: price };
});
